import clsx from 'clsx';
import Style from './FormScore.module.scss';
import { isNil } from 'lodash';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { ReactComponent as Tip } from 'assets/img/common/tip_filled_20.svg';
import { ReactComponent as Trigon } from 'assets/img/common/trigon_filled_24.svg';
import { Tooltip } from 'src/components/uikit/tooltip/tooltip.component';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useCommonLocalization } from 'src/application/localisation/useCommonLocalization';
import { logger } from 'src/application/logging/logging';
import { useQueryClient } from '@tanstack/react-query';
import {
  IFormResultVersionDto,
  IFormScore,
  IScoreSection,
  IScoreSectionConfig,
  ScoreCalculationStatusEnum,
  ScoreGroupEnum,
  ScoreSectionLayout,
  StudyRoleClaimValue,
} from 'src/services/api/api-client';
import { LocalizedResourceDictionaryKeys } from 'src/application/localisation/i18next';
import { getFormResultByIdQueryKey } from 'src/services/api/api-client/FormsQuery';
import { recalculateScore } from 'src/services/api/api-client/FormsClient';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { AppButton } from 'uikit/buttons';

type FormScoreComponentProps = {
  formResultVersion?: IFormResultVersionDto;
  scoreSectionConfig: IScoreSectionConfig | null;
};

type ScoreItemType = {
  text: string;
  score: number | undefined | null;
  decimal?: number | null;
};

export const FormScoreComponent: FC<FormScoreComponentProps> = ({ formResultVersion, scoreSectionConfig }) => {
  const { t } = useScopedTranslation('Forms');
  const commonLocalizer = useCommonLocalization();

  const scoreSections: ScoreItemType[] | undefined = useMemo(
    () =>
      formResultVersion?.score?.sectionScore?.map((section: IScoreSection) => ({
        text: commonLocalizer(
          `Forms.Score.ScoreGroup.${ScoreGroupEnum[section.scoreGroup]}` as LocalizedResourceDictionaryKeys,
        ),
        score: section.score,
        decimal: formResultVersion?.score?.decimal,
      })),
    [commonLocalizer, formResultVersion],
  );

  const totalScore: ScoreItemType | null = useMemo(
    () =>
      !isNil(formResultVersion?.score?.totalScore)
        ? {
            text: t('Score.TotalScore'),
            score: formResultVersion?.score?.totalScore,
            decimal: formResultVersion?.score?.decimal,
          }
        : null,
    [formResultVersion, t],
  );

  if (
    formResultVersion?.scoreStatus === ScoreCalculationStatusEnum.InProcess ||
    formResultVersion?.scoreStatus === ScoreCalculationStatusEnum.Error
  ) {
    return (
      <div className={Style.scoreErrorContainer}>
        <Trigon className={Style.redIcon} />
        <div>
          {formResultVersion?.canRetryScoreCalculation
            ? t('Score.ScoreCalculation.ErrorCalculation')
            : t('Score.ScoreCalculation.CantRetryMessage')}
        </div>
        <div className={Style.retryButton}>
          <FormScoreRetryComponent formResultVersion={formResultVersion} scoreSectionConfig={scoreSectionConfig} />
        </div>
      </div>
    );
  }

  if (!formResultVersion?.score) return null;

  if (scoreSectionConfig)
    return (
      <Wrapper disable={!scoreSections || scoreSections.length === 0}>
        <GroupedScoreSection scoreSectionConfig={scoreSectionConfig} formScore={formResultVersion?.score} />
        <TotalScoreSection totalScore={totalScore} />
      </Wrapper>
    );

  return (
    <Wrapper disable={!scoreSections || scoreSections.length === 0}>
      <ScoreRow title={null} tooltip={null} sections={scoreSections} />
      <TotalScoreSection totalScore={totalScore} />
    </Wrapper>
  );
};

const Wrapper: FC<PropsWithChildren<{ disable?: boolean }>> = ({ children, disable }) => {
  const { t } = useScopedTranslation('Forms.Score');
  const [isHidden, setIsHidden] = useState(false);

  if (disable) return <>{children}</>;

  return (
    <div className={Style.wrapper}>
      <AppButton
        variant={'icon-link'}
        colorSchema={'primary'}
        text={isHidden ? t('ShowScoreButtontext') : t('HideScoreButtontext')}
        className={Style.hideScoreButton}
        onClick={() => setIsHidden((x) => !x)}
      />
      <div data-is-hidden={isHidden} className={Style.collapsibleContainer}>
        {children}
      </div>
    </div>
  );
};

const TotalScoreSection: FC<{ totalScore: ScoreItemType | null }> = ({ totalScore }) => {
  if (!totalScore) return null;

  return (
    <div className={Style.totalScoreSection}>
      <div className={Style.text}>{totalScore.text}</div>
      <div className={Style.score}>
        {!isNil(totalScore.score) ? totalScore.score?.toFixed(totalScore.decimal ?? 0) : <EmptyScore />}
      </div>
    </div>
  );
};

const GroupedScoreSection: FC<{
  scoreSectionConfig: IScoreSectionConfig;
  formScore: IFormScore | undefined;
}> = ({ scoreSectionConfig, formScore }) => {
  const { t } = useScopedTranslation('Forms.Score');

  const groupedSections = useMemo(
    () =>
      scoreSectionConfig.scoreSectionGroups
        ?.map((x) => ({
          title: x.title,
          tooltip: x.tooltip,
          sections: x.sections
            .map((key) => formScore?.sectionScore?.find((score) => score.scoreGroup === key))
            .filter(Boolean)
            .map((section: IScoreSection | undefined) => ({
              text: t(`ScoreGroup.${ScoreGroupEnum[section!.scoreGroup]}` as LocalizedResourceDictionaryKeys),
              score: section!.score,
              decimal: formScore?.decimal,
            })),
        }))
        .filter((group) => group.sections.length > 0),
    [t, scoreSectionConfig.scoreSectionGroups, formScore],
  );

  return (
    <div
      className={clsx(Style.groupedScoreContainer, {
        [Style.horizontal]: scoreSectionConfig.layout === ScoreSectionLayout.Horizontal,
        [Style.vertical]: scoreSectionConfig.layout === ScoreSectionLayout.Vertical,
      })}
    >
      {groupedSections?.map((group) => (
        <ScoreRow key={group.title} title={group.title} tooltip={group.tooltip} sections={group.sections} />
      ))}
    </div>
  );
};

const EmptyScore: FC = () => {
  const commonLocalizer = useCommonLocalization();
  const { t } = useScopedTranslation('Forms');

  return (
    <div className={Style.emptyScore}>
      {commonLocalizer('Common_dash')}
      <Tooltip text={t('Score.EmptyScoreTooltip')}>
        <Tip className={Style.tip} />
      </Tooltip>
    </div>
  );
};

const FlexScoreItem: FC<ScoreItemType & { className?: string }> = ({ text, className, score, decimal }) => {
  return (
    <div className={clsx(Style.scoreSectionVertical, className)} data-test-id={'score-group'}>
      <div data-test-id={'score'} className={Style.score}>
        {!isNil(score) ? score?.toFixed(decimal ?? 0) : <EmptyScore />}
      </div>
      <div className={Style.text}>{text}</div>
    </div>
  );
};

const GridScoreItem: FC<ScoreItemType & { isTotalScore?: boolean }> = ({ text, score, decimal, isTotalScore }) => {
  const commonLocalizer = useCommonLocalization();
  const { t } = useScopedTranslation('Forms');

  const emptyScore = useMemo(() => {
    return (
      <div className={Style.emptyScore}>
        <Tooltip text={t('Score.EmptyScoreTooltip')}>
          <Tip className={Style.tip} />
        </Tooltip>
        {commonLocalizer('Common_dash')}
      </div>
    );
  }, [commonLocalizer, t]);

  return (
    <div className={Style.scoreSection} data-is-total-score={!!isTotalScore} data-test-id={'score-group'}>
      <div className={Style.text}>{text}</div>
      <div data-test-id={'score'} className={Style.score}>
        {!isNil(score) ? score?.toFixed(decimal ?? 0) : emptyScore}
      </div>
    </div>
  );
};

const ScoreRow: FC<{ title: string | null; tooltip: string | null; sections: ScoreItemType[] | undefined }> = ({
  title,
  tooltip,
  sections,
}) => {
  const { t } = useScopedTranslation('Forms.Score');
  if (!sections || sections.length === 0) return null;

  return (
    <div data-test-id={'score-group-section'} className={Style.scoreGroup}>
      <div className={Style.titleWithTooltip}>
        <div className={Style.groupTitle}>{title ?? t('ScoreTitle')}</div>
        {!!tooltip && (
          <Tooltip text={tooltip} hostStyles={Style.groupTooltip}>
            <Tip className={Style.tip} />
          </Tooltip>
        )}
      </div>
      <div className={Style.items}>
        {sections.map((section) => (
          <FlexScoreItem key={section.text} {...section} className={Style.item} />
        ))}
      </div>
    </div>
  );
};

const FormScoreRetryComponent: FC<FormScoreComponentProps> = ({ formResultVersion }) => {
  const { t } = useScopedTranslation('Forms.Score.ScoreCalculation');
  const { hasStudyClaim } = useHasStudyClaims();
  const client = useQueryClient();
  const [calculating, setCalculating] = useState<boolean>(false);

  const isInProgress = useMemo(
    () => formResultVersion?.scoreStatus === ScoreCalculationStatusEnum.InProcess || calculating,
    [calculating, formResultVersion?.scoreStatus],
  );

  const recalculateFormScore = useCallback(async () => {
    if (!formResultVersion) return;

    setCalculating(true);
    try {
      await recalculateScore(
        formResultVersion.formResultId,
        formResultVersion.versionNumber,
        formResultVersion.minorVersionNumber,
      );
      await client.invalidateQueries(getFormResultByIdQueryKey(formResultVersion.formResultId));
    } catch (error: any) {
      logger().error(error);
    } finally {
      setCalculating(false);
    }
  }, [client, formResultVersion]);

  if (!formResultVersion?.canRetryScoreCalculation || !hasStudyClaim(StudyRoleClaimValue.PatientCreate)) return <></>;

  if (isInProgress) return <div className={Style.inProgressText}>{t('InProgress')}</div>;

  return (
    <AppButton
      variant={'icon-link'}
      colorSchema={'primary'}
      text={t('Retry')}
      onClick={recalculateFormScore}
      disabled={calculating}
      isLoading={isInProgress}
    />
  );
};
